import { useParams } from "react-router-dom";

import {
  type McpTenantDetail,
  type McpTierHistory,
  type McpUsage,
} from "../../api/mcp";
import {
  LABEEB_CATALOG,
  labeebTenantPath,
  type LabeebCatalog,
  type LabeebTenantDetail,
} from "../../api/labeeb";
import { RecentChanges } from "../../components/audit/RecentChanges";
import { Breadcrumbs } from "../../components/Layout";
import { NotSeenRecently, ResourceView } from "../../components/states";
import { Page } from "../../components/ui";
import type { AppConfig } from "../../config";
import { Button } from "../../f7";
import { useResource } from "../../hooks/useResource";
import { LabeebRecord } from "./record/LabeebRecord";
import { McpRecord } from "./record/McpRecord";

export function Tenant({ config }: { config: AppConfig }) {
  const { ref = "" } = useParams();
  const enc = encodeURIComponent(ref);

  const tenant = useResource<McpTenantDetail>(config, `/mcp/admin/tenant/${enc}`, [ref]);
  const usage = useResource<McpUsage>(config, `/mcp/admin/tenant/${enc}/usage`, [ref]);
  const history = useResource<McpTierHistory>(config, `/mcp/admin/tenant/${enc}/history`, [ref]);

  // Labeeb's view of the SAME merchant. Both products key on the business
  // reference, so one id reaches both — which is what makes a tier split
  // ("Pro in one product, Plus in the other") visible at last.
  //
  // A 404 here means Labeeb has no record of this business. The standing line
  // says that. It is not a lookup failure, which stays "Could not read".
  const labeeb = useResource<LabeebTenantDetail>(config, labeebTenantPath(ref), [ref]);
  const labeebCatalog = useResource<LabeebCatalog>(config, LABEEB_CATALOG);

  const s = tenant.state;

  return (
    <Page title={<span className="ref">{ref}</span>}>
      <Breadcrumbs
        trail={[
          { label: "Overview", to: "/" },
          { label: "Tenants", to: "/tenants" },
          { label: ref },
        ]}
      />

      {/* A 404 still offers the operation that WORKS on a 404 merchant.
          Blocking is keyed by reference and never needs the ledger. */}
      <ResourceView
        state={s}
        what="this merchant"
        onRetry={tenant.reload}
        notFound={
          <NotSeenRecently
            reference={ref}
            actions={
              <Button outline href={`/tenants/${enc}/users`}>
                Manage blocks for this reference
              </Button>
            }
          />
        }
      />

      {s.kind === "ready" &&
        (() => {
          const d = s.data;
          // Labeeb's key for this merchant, when Labeeb holds one at all. A
          // 404 from Labeeb is the normal "no config item" case, not an
          // error, so this stays null rather than rendering an empty row.
          const labeebTenantId =
            labeeb.state.kind === "ready" ? (labeeb.state.data?.tenant_id ?? null) : null;

          return (
            // The Page column already owns the rhythm at `gap-6`; `space-y-5`
            // here set a second, different one, so the tenant record was the
            // only page whose blocks sat closer together than the rest.
            <div className="flex flex-col gap-6">
              {/* The design's IDENTIFIER STRIP: a compact filled band of
                  labelled keys, not rows in the same list as the controls. The
                  identifiers are what you copy into a ticket; the tier picker
                  and the block button are what you act with, and interleaving
                  them in one `dl` made a two-line field list eight rows long
                  and buried both. */}
              <div className="flex flex-wrap gap-x-8 gap-y-3 rounded-lg bg-slate-50 p-4 ring-1 ring-slate-950/5 dark:bg-white/5 dark:ring-white/10">
                {([
                  ["Name", d.business_name, false],
                  ["business_reference", d.business_reference, true],
                  ["Owner email", d.email, true],
                  // Labeeb's own key, shown only when Labeeb knows this
                  // merchant. An operator who arrived from that side holds
                  // this id and nothing else, and it need not equal the
                  // business reference — so a blank row here would read as
                  // "Labeeb has no id" rather than "Labeeb has no merchant".
                  //
                  // `business_id` is deliberately NOT here: it is a UUID,
                  // searchable but never rendered. See `api/mcp.ts`.
                  ...(labeebTenantId
                    ? [["Labeeb tenant_id", labeebTenantId, true] as const]
                    : []),
                ] as const).map(([label, value, mono]) => (
                  <div key={label as string} className="min-w-0">
                    <p className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase dark:text-slate-500">
                      {label as string}
                    </p>
                    <p
                      className={`mt-0.5 text-sm text-slate-950 dark:text-white ${mono ? "font-mono" : ""}`}
                      dir="auto"
                    >
                      {value as string}
                    </p>
                  </div>
                ))}
              </div>

              {/* ONE BLOCK PER PRODUCT, each self-contained.
                  A reader debugging "MCP is wrong for this merchant" opens one
                  block and finds its tier, access, usage, caps and history
                  together, rather than picking them out of a pile of cards
                  interleaved with the other product's.

                  Each block still shows BOTH tiers in its Plan section — a
                  merchant on Pro in one product and Plus in the other is a
                  `tier split`, and splitting the page by product is exactly
                  what would hide it. */}
              <McpRecord
                config={config}
                reference={ref}
                detail={d}
                usage={usage}
                history={history}
                reloadDetail={tenant.reload}
              />
            </div>
          );
        })()}

      {/* OUTSIDE the MCP gate on purpose — see LabeebRecord. */}
      <LabeebRecord
        config={config}
        tenantId={ref}
        labeeb={labeeb}
        labeebCatalog={labeebCatalog}
        mcpTier={s.kind === "ready" ? (s.data.tier ?? null) : null}
      />

      <div className="mt-4">
        <RecentChanges config={config} domain="tenant" target={ref} />
      </div>

    </Page>
  );
}

